import {useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import Switch from "@mui/material/Switch";
import SoftInput from "components/SoftInput";
import logo from "assets/images/logo.png";
import {login} from "../../../axios-client";
import {useSoftUIController} from "../../../context";
import {setLayout, setToken, setUserInfo} from "../../../store/actions/app-actions";
import Alert from "@mui/material/Alert";
import {FREE_AGENT} from "../../../utlis/constant";
import { useTheme } from "@mui/material/styles";
import {
    Grid,
    Box,
    Typography,
    Button,
    IconButton,
} from "@mui/material";
import { ArrowRight, ArrowLeft, Eye, EyeOff } from "lucide-react";
import Card from "@mui/material/Card";
import LanguageSwitcher from "../../../reusableComponents/LanguageSwitcher/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";

function SignIn() {
    const theme = useTheme();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [role, setRole] = useState("free-agent");
    const [controller, dispatch] = useSoftUIController();
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();


    const [animateCard, setAnimateCard] = useState(false);
    const [animateTitle, setAnimateTitle] = useState(false);
    const [animateSubtext, setAnimateSubtext] = useState(false);
    const [animateForm, setAnimateForm] = useState(false);
    const [animateImage, setAnimateImage] = useState(false);
    const [animateLogo, setAnimateLogo] = useState(false);

    const {i18n} = useTranslation();
    const { t } = useTranslation(['signIn']);
    const dir = i18n.dir();
    const currentLang = i18n.language;
    const features = t('panel.features', { returnObjects: true });
    const signUpPage = `/${currentLang}/authentication/sign-up`;
    const handleSetRememberMe = () => setRememberMe(!rememberMe);

    useEffect(() => {
        setTimeout(() => setAnimateCard(true), 100);
        setTimeout(() => setAnimateLogo(true), 300);
        setTimeout(() => setAnimateImage(true), 400);
        setTimeout(() => setAnimateTitle(true), 500);
        setTimeout(() => setAnimateSubtext(true), 600);
        setTimeout(() => setAnimateForm(true), 700);
        setErrorMessage(null);
    }, [email, password]);


    useEffect(() => {
        setLayout(dispatch, "page");
    }, []);

    const handleLogin = (event) => {
        event.preventDefault();
        setIsLoading(true);
        login(email, password, FREE_AGENT)
            .then((response) => {
                setUserInfo(dispatch, response?.data);
                setToken(dispatch, response.data?.access_token);
                localStorage.setItem("token", response.data?.access_token);
                navigate(`/${currentLang}/documents`);
            })
            .catch((error) => {
                if (error.response && error.response.data && error.response.data.message) {
                    setErrorMessage(error.response.data.message);
                } else {
                    setErrorMessage('An error occurred. Please try again later.');
                }
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <>
            <style>
                {`
          @keyframes fadeSlideIn {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
         @keyframes scan {
            0% { top: 0;} 
            50% { top: 100%;}
            100% { top: 0;}
            }
        `}
            </style>



            <Grid container sx={{ minHeight: "100vh", overflow: "hidden" }} dir={dir}>

                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "#ffffff",
                        position: "relative",
                        p: 5,
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: theme.spacing(2),
                            left: theme.spacing(2),
                            right: theme.spacing(2),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            zIndex: 10,
                        }}
                    >
                        <Box
                            sx={{
                                opacity: animateLogo ? 1 : 0,
                                transform: animateLogo ? "translateY(0)" : "translateY(-10px)",
                                transition: "all 0.8s ease",
                            }}

                        >
                            <img src={logo} alt="YaiGlobal Logo" style={{ height: 48 }} />
                        </Box>

                        <LanguageSwitcher />
                    </Box>

                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: 480,
                            textAlign: "center",
                            paddingTop:"15vh"
                        }}
                    >
                        <Typography
                            variant="h3"
                            fontWeight="bold"
                            sx={{
                                background: "linear-gradient(to right, #d946ef, #ec4899)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                mb: 1,
                                opacity: animateTitle ? 1 : 0,
                                transform: animateTitle ? "translateY(0)" : "translateY(10px)",
                                transition: "all 0.7s ease",
                            }}
                        >
                            {t("login.welcome")}
                        </Typography>

                        <Typography
                            variant="body1"
                            color="textSecondary"
                            sx={{
                                mb: 3,
                                opacity: animateSubtext ? 1 : 0,
                                transform: animateSubtext ? "translateY(0)" : "translateY(10px)",
                                transition: "all 0.8s ease",
                            }}
                        >
                            {t("login.description")}
                        </Typography>

                        {errorMessage && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
                                {t("login.errors.invalidCredentials")}
                            </Alert>
                        )}

                        <Card
                            elevation={4}
                            sx={{
                                borderRadius: 3,
                                p: 4,
                                opacity: animateForm ? 1 : 0,
                                transform: animateForm ? "translateY(0)" : "translateY(10px)",
                                transition: "all 0.7s ease",
                            }}
                        >
                            <Box component="form" onSubmit={handleLogin} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                <SoftInput
                                    type="email"
                                    placeholder={t("login.email")}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />

                                <Box sx={{ position: "relative" }}>

                                    <SoftInput
                                        type={showPassword ? "text" : "password"}
                                        placeholder={t("login.password")}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        sx={{
                                            position: "absolute",
                                            ...(i18n.language === "ar" ? {left:8} : {right:8}),
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            padding: 0,
                                            color: "#6b7280",
                                        }}
                                    >
                                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </IconButton>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Switch
                                            checked={rememberMe}
                                            onChange={() => setRememberMe(!rememberMe)}
                                            sx={{
                                                color: "#d946ef",
                                                "&.Mui-checked": { color: "#d946ef" },
                                                "&.Mui-checked + .MuiSwitch-track": {
                                                    backgroundColor: "#d946ef",
                                                },
                                            }}
                                        />
                                        <Typography variant="caption" sx={{ ml: 1, marginRight: "5px"}}>
                                            {t("login.rememberMe")}
                                        </Typography>
                                    </Box>

                                    <Typography
                                        variant="caption"
                                        sx={{ color: "#d946ef", cursor: "pointer" }}
                                        onClick={() => navigate(`/${currentLang}/authentication/reset-password`)}
                                    >
                                        {t("login.forgotPassword")}
                                    </Typography>
                                </Box>

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={isLoading}
                                    sx={{
                                        height: 45,
                                        background: "linear-gradient(90deg, #d946ef, #ec4899)",
                                        color: "#fff",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        "&:hover": {
                                            background: "linear-gradient(90deg, #b91cdb, #d946ef)",
                                        },
                                    }}
                                    endIcon={
                                        <Box component="span" sx={{ ml: dir === "ltr" ? 1 : 0, mr: dir === "rtl" ? 2 : 0 }}>
                                          {dir === "rtl" ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                                        </Box>
                                    }
                                >
                                    {isLoading ? t("login.signingIn") : t("login.signIn")}
                                </Button>
                                <Typography variant="caption" align="center" sx={{ mt: 2 }}>
                                    {t("login.noAccount")}{" "}
                                    <Link to={signUpPage} style={{ color: "#d946ef", textDecoration: "none", fontWeight: 500 }}>
                                        {t("login.signUp")}
                                    </Link>
                                </Typography>
                            </Box>
                        </Card>
                        <Footer />
                    </Box>
                </Grid>



                {/* Right Side Decorative Panel */}
                <Grid item md={6} sx={{ display: { xs: "none", md: "block" }, position: "relative", overflow: "hidden" }}>
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            background: "linear-gradient(135deg, rgba(217,70,239,0.9), rgba(126,34,206,0.9))",
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: animateImage ? 0.8 : 0,
                            transition: "opacity 1s ease",
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                zIndex: 1,
                                p: 4,
                                maxWidth: 500,
                                backgroundColor: "rgba(255,255,255,0.1)",
                                borderRadius: 4,
                                border: "1px solid rgba(255,255,255,0.2)",
                                animation: animateImage ? "fadeSlideIn 1s ease forwards" : "none",
                            }}
                        >
                            <Typography variant="h6" fontWeight="bold" sx={{ color: "#fff", mb: 2 }}>
                                {t("panel.brand")}
                            </Typography>
                            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: "#fff" }}>
                                {t("panel.headline")}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mb: 3 }}>
                                {t("panel.body")}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                {features.map((label) => (
                                    <Box
                                        key={label}
                                        sx={{
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: 50,
                                            backgroundColor: "rgba(255,255,255,0.2)",
                                            fontSize: "0.875rem",
                                            color: "#fff",
                                        }}
                                    >
                                        {label}
                                    </Box>
                                ))}
                            </Box>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    width: '100%',
                                    height: '1.5px',
                                    backgroundColor: 'rgba(250, 204, 21, 0.7)',
                                    boxShadow: '0 0 8px 2px rgba(250, 204, 21, 0.5)',
                                    animation: 'scan 20s ease-in-out infinite',
                                }}
                            />

                        </Box>
                    </Box>

                    <Box
                        component="svg"
                        xmlns="http://www.w3.org/2000/svg"
                        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.2 }}
                    >
                        <defs>
                            <pattern id="circuit-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                                <path
                                    d="M10,10 L40,10 L40,40 L70,40 L70,70 L40,70 L40,40 M60,10 L90,10 L90,40 M10,30 L10,60 L30,60 M10,80 L40,80 L40,90 L70,90 M60,30 L60,60 L90,60 L90,90"
                                    fill="none"
                                    stroke="white"
                                    strokeWidth="1"
                                />
                            </pattern>
                        </defs>
                        <rect x="0" y="0" width="100%" height="100%" fill="url(#circuit-pattern)" />
                    </Box>
                </Grid>
            </Grid>

        </>
    );
}

export default SignIn;
